import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useReducer
} from 'react';
import useGlobalVariables from '../../../hooks/useGlobalVariables';
import useTranslations from '../../../hooks/useTranslations';
import fetchAPI from '../../../utils/fetchAPI';
import generateId from '../../../utils/generateId';
import sortInfoNames from '../../../utils/sortInfoNames';
import SubmitterQueryBuilderView from '../view/SubmitterQueryBuilderView';
import { ANONYMOUS_USER_ID } from '../../../utils/Constants';
// [{"field":"'submitterId'","op":"=","value":"852e2de8-2410-49e2-931e-6d59e481a05b"}]

const SubmitterQueryBuilderReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const initialState = {
  internOp: 'or',
  users: {}
};

const SubmitterQueryBuilderEngine = ({
  query = [],
  update = () => {},
  sentencePrefix,
  form = {}
}) => {
  const [data, dispatch] = useReducer(
    SubmitterQueryBuilderReducer,
    initialState
  );
  const [inDeleteSequence, setInDeleteSequence] = useState(null);
  const [hoverOn, setHoverOn] = useState(null);

  const t = useTranslations();
  const { setIsLoading } = useGlobalVariables();
  const skipUpdate = useRef(true);

  useEffect(() => {
    if (skipUpdate.current) {
      skipUpdate.current = false;
      return;
    }
    const query2Export = Object.values(data.users).map(u => ({
      field: "'submitterId'",
      op: '=',
      value: u.value,
      internOp: data.internOp
    }));

    update(query2Export);
  }, [data]);

  useEffect(() => {
    if (!!query.length) {
      const usersPromises = query.map(qBlock => {
        const userId = qBlock.value;
        if (!!!userId) {
          return null;
        }

        if (userId === ANONYMOUS_USER_ID) {
          return {
            value: userId,
            data: {
              firstName: 'Anonymous',
              lastName: 'User',
              email: ''
            }
          };
        }

        return new Promise((res, rej) => {
          fetchAPI(`/users/v1/users/${userId}`).then(r => {
            const info = r.info;
            if (!!!info) {
              return;
            }

            const user = {
              value: userId,
              data: {
                firstName: info.firstName,
                lastName: info.lastName,
                email: info.email
              }
            };

            res(user);
          });
        });
      });

      setIsLoading(true);
      Promise.all(usersPromises)
        .then(usersArr => {
          const users = {};

          usersArr.forEach(u => (users[`id-${generateId()}`] = !!u ? u : {}));

          skipUpdate.current = true;

          dispatch({
            type: 'update',
            payload: {
              users
            }
          });
        })
        .finally(() => setIsLoading(false));
    }
  }, []);

  const getMatchingUsers = useCallback(
    (v = '', currentValues = null) => {
      const formId = form.id;
      return fetchAPI(
        `/forms/v1/forms/${formId}/users?offset=0&limit=100&sort='status' asc&query=[
        [{"field":"'status'","op":"=","value":"'ACTIVE'"}],
        [
          {"field": "'info'->'email'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"},
          {"field": "'info'->'lastName'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"},
          {"field": "'info'->'userName'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"},
          {"field": "'info'->'firstName'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"}
      ]
    ]`.replace(/\n\s+/g, '    ')
      ).then(({ results }) => {
        let assignedUserEmail, mapResults, fullResults;
        assignedUserEmail =
          currentValues && currentValues.info ? currentValues.info.email : '';
        mapResults = results
          .sort(sortInfoNames)
          .filter(u => u.info.email !== assignedUserEmail)
          .map(u => {
            return {
              text: u.info.firstName + ' ' + u.info.lastName,
              value: {
                label: null,
                value: u.id,
                data: {
                  firstName: u.info.firstName,
                  lastName: u.info.lastName,
                  email: u.info.email
                }
              }
            };
          });
        fullResults = [];
        if (currentValues) {
          fullResults = [
            {
              text:
                currentValues.info.firstName +
                ' ' +
                currentValues.info.lastName,
              value: {
                label: null,
                value: currentValues.submitterId,

                data: {
                  firstName: currentValues.info.firstName,
                  lastName: currentValues.info.lastName,
                  email: currentValues.info.email,
                  current: true
                }
              }
            }
          ];
        }

        fullResults = [
          ...fullResults,
          ...mapResults,
          {
            text: 'Anonymous User',
            value: {
              label: null,
              value: ANONYMOUS_USER_ID,
              data: {
                firstName: 'Anonymous',
                lastName: 'User',
                email: ''
              }
            }
          }
        ];
        return fullResults;
      });
    },
    [fetchAPI, form]
  );

  const removeCondition = useCallback(
    id => {
      const usersCpy = JSON.parse(JSON.stringify(data.users));
      delete usersCpy[id];
      dispatch({ type: 'update', payload: { users: usersCpy } });
    },
    [data]
  );

  const setUser = useCallback(
    (id, user) => {
      const userCpy = JSON.parse(JSON.stringify(data.users));
      userCpy[id] = user;
      dispatch({ type: 'update', payload: { users: userCpy } });
    },
    [data]
  );
  const addCondition = useCallback(() => {
    const userCpy = JSON.parse(JSON.stringify(data.users));
    userCpy[`id-${generateId()}`] = {};
    skipUpdate.current = true;
    dispatch({ type: 'update', payload: { users: userCpy } });
  }, [data]);

  const onRemoveConditionClicked = useCallback(
    id => {
      if (Object.keys(data.users).length === 1) {
        // we're deleting the last (sub-)condition - we want to use the
        // condition delete and not sub-condition delete.
        // We trigger it by sending empt query
        update([]);
        return;
      }
      setInDeleteSequence(id);
    },
    [data, update]
  );

  const viewProps = {
    sentencePrefix: sentencePrefix || t('logic.when_submitter'),
    getMatchingUsers,
    ...data,
    inDeleteSequence,
    removeCondition,
    hoverOn,
    setHoverOn,
    setUser,
    onRemoveConditionClicked,
    addCondition
  };

  return <SubmitterQueryBuilderView {...viewProps} />;
};

export default SubmitterQueryBuilderEngine;
